import React, {createRef, ReactElement} from "react";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "shared/BaseFragment";
import {StyledBoxColumn, StyledBoxColumnWithLabel, StyledBoxRow} from "shared/StyledComponents";
import {Button, Slider, Typography} from "@mui/material";
import {DIVIDER_XXBLACK} from "shared/dimens";
import {Lipsync, VISEMES} from "wawa-lipsync";
import {FaceModel, FaceModelBrows, FaceModelEyes, FaceModelMouth} from "./types";
import {ChatPersonaType} from "../../toktiv-common/types";
import {CHAT_PERSONA_APPEARANCE_KTM} from "../types";

function FaceModelControlSlider({label, min, max, value, setValue}: {
  label: string,
  min: number,
  max: number,
  value: number,
  setValue: (value: number) => void
}): ReactElement {
  return <StyledBoxRow style={{alignItems: "center"}}>
    <Typography variant={"caption"} style={{width: 196}}>{label}</Typography>
    <Slider min={min} max={max} step={0.01} value={value} onChange={(e, newValue: number) => setValue(newValue)}/>
  </StyledBoxRow>;
}

function updateFaceModelEyes(faceModel: FaceModel, eyes: Partial<FaceModelEyes>): FaceModel {
  return {
    ...faceModel,
    eyes: {
      ...faceModel.eyes,
      ...eyes,
    }
  };
}

function updateFaceModelBrows(faceModel: FaceModel, brows: Partial<FaceModelBrows>): FaceModel {
  return {
    ...faceModel,
    brows: {
      ...faceModel.brows,
      ...brows,
    }
  };
}

function updateFaceModelMouth(faceModel: FaceModel, mouth: Partial<FaceModelMouth>): FaceModel {
  const model: FaceModelMouth = {
    ...faceModel.mouth,
    ...mouth,
  };
  return {
    ...faceModel,
    mouth: model,
  };
}

function FaceModelControls({viseme, setViseme, saveViseme, faceModel, setFaceModel}: {
  viseme: VISEMES,
  setViseme: (viseme: VISEMES) => void,
  saveViseme?: (viseme: VISEMES, model: FaceModelMouth) => void,
  faceModel: FaceModel,
  setFaceModel: (faceModel: FaceModel) => void
}): ReactElement {
  function maybeSaveViseme(viseme: VISEMES, model: FaceModel): FaceModel {
    saveViseme?.(viseme, model.mouth);
    return model;
  }

  return <StyledBoxColumn style={{width: 320}}>
    <StyledBoxColumnWithLabel label={"Eyes"}>
      <FaceModelControlSlider
        label={"Open"}
        min={0}
        max={1}
        value={faceModel.eyes.open}
        setValue={value => setFaceModel(updateFaceModelEyes(
          faceModel,
          {
            open: value
          }
        ))}/>
      <FaceModelControlSlider
        label={"Separation"}
        min={-1}
        max={1}
        value={faceModel.eyes.separation}
        setValue={value => setFaceModel(updateFaceModelEyes(
          faceModel,
          {
            separation: value
          }
        ))}/>
      <FaceModelControlSlider
        label={"Look Offset"}
        min={-1}
        max={1}
        value={faceModel.eyes.lookOffset}
        setValue={value => setFaceModel(updateFaceModelEyes(
          faceModel,
          {
            lookOffset: value
          }
        ))}/>
      <FaceModelControlSlider
        label={"Look Offset"}
        min={-Math.PI}
        max={Math.PI}
        value={faceModel.eyes.lookAngle}
        setValue={value => setFaceModel(updateFaceModelEyes(
          faceModel,
          {
            lookAngle: value
          }
        ))}/>
    </StyledBoxColumnWithLabel>
    <StyledBoxColumnWithLabel label={"Brows"}>
      <FaceModelControlSlider
        label={"Offset"}
        min={0}
        max={1}
        value={faceModel.brows.offset}
        setValue={value => setFaceModel(updateFaceModelBrows(
          faceModel,
          {
            offset: value
          }
        ))}/>
      <FaceModelControlSlider
        label={"Angle"}
        min={-Math.PI / 4}
        max={Math.PI / 4}
        value={faceModel.brows.angle}
        setValue={value => setFaceModel(updateFaceModelBrows(
          faceModel,
          {
            angle: value
          }
        ))}/>
    </StyledBoxColumnWithLabel>
    <StyledBoxColumnWithLabel label={"Mouth"}>
      <div>
        {Object.getOwnPropertyNames(VISEMES).map(name => {
          return <Button
            onClick={() => setViseme(VISEMES[name])}
            variant={VISEMES[name] === viseme ? "contained" : null}>
            {name}
          </Button>;
        })}
      </div>
      <FaceModelControlSlider
        label={"Open"}
        min={0}
        max={1}
        value={faceModel.mouth.open}
        setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
          faceModel,
          {
            open: value
          },
        )))}/>
      <FaceModelControlSlider
        label={"Upper Lip"}
        min={0}
        max={1}
        value={faceModel.mouth.upperLip}
        setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
          faceModel,
          {
            upperLip: value
          },
        )))}/>
      <FaceModelControlSlider
        label={"Lower Lip"}
        min={0}
        max={1}
        value={faceModel.mouth.lowerLip}
        setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
          faceModel,
          {
            lowerLip: value
          },
        )))}/>
    </StyledBoxColumnWithLabel>
    <FaceModelControlSlider
      label={"Rounded"}
      min={0}
      max={1}
      value={faceModel.mouth.rounded}
      setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
        faceModel,
        {
          rounded: value
        },
      )))}/>
    <FaceModelControlSlider
      label={"Smiling"}
      min={0}
      max={1}
      value={faceModel.mouth.smiling}
      setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
        faceModel,
        {
          smiling: value
        },
      )))}/>
    <FaceModelControlSlider
      label={"Skew"}
      min={0}
      max={1}
      value={faceModel.mouth.skew}
      setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
        faceModel,
        {
          skew: value
        },
      )))}/>
    <FaceModelControlSlider
      label={"Rotate"}
      min={0}
      max={1}
      value={faceModel.mouth.rotate}
      setValue={value => setFaceModel(maybeSaveViseme(viseme, updateFaceModelMouth(
        faceModel,
        {
          rotate: value
        },
      )))}/>
  </StyledBoxColumn>
}

export type AvatarProps = BaseFragmentProps & {
  personaType: ChatPersonaType,
  size: number,
  visemeMap: Map<VISEMES, FaceModelMouth>,
  audio: HTMLAudioElement,
  src?: string,
}

type AvatarState = BaseFragmentState & {
  faceModel: FaceModel,
  blink?: boolean,
  blinkChangeAt?: number,
  blinkOnAt?: number,
  viseme: VISEMES,
}

type Point = { x: number, y: number }

export class Avatar extends BaseFragment<AvatarProps, AvatarState> {

  private readonly canvasRef = createRef<HTMLCanvasElement>();
  private readonly lipsyncManager = new Lipsync();

  protected onCreateState(props?: AvatarProps): AvatarState {
    const viseme = VISEMES.sil;
    return {
      ...super.onCreateState(props),
      faceModel: {
        eyes: {
          open: 1,
          separation: 0,
          lookOffset: 0,
          lookAngle: 0,
        },
        brows: {
          offset: .25,
          angle: Math.PI / 16,
        },
        mouth: this.props.visemeMap.get(viseme),
      },
      viseme: viseme,
    };
  }

  componentDidUpdate(prevProps: Readonly<AvatarProps>, prevState: Readonly<AvatarState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevProps.src !== this.props.src) {
      this.play();
    }
    let faceModel: FaceModel = this.state.faceModel;
    if (prevState.viseme !== this.state.viseme) {
      faceModel = updateFaceModelMouth(this.state.faceModel, this.props.visemeMap.get(this.state.viseme));
    }
    if (prevState.blink !== this.state.blink) {
      faceModel = updateFaceModelEyes(this.state.faceModel, {open: this.state.blink ? 0 : 1});
    }
    if (faceModel !== this.state.faceModel) {
      this.setState({faceModel});
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    const appearance = CHAT_PERSONA_APPEARANCE_KTM.findMetadata(this.props.personaType);
    const hairId = appearance.avatarConfig.parts.find(part => part.id === "hair")?.value || 0;
    this.drawCanvas();
    return <StyledBoxColumn style={{gap: 48, justifyContent: "center"}}>
      <div style={{position: "relative", zoom: this.props.size / 400}}>
        <canvas ref={this.canvasRef} width={400} height={400}
                style={{
                  verticalAlign: "top",
                  background: "linear-gradient(#fdb, #fed)",
                  borderRadius: 128, border: DIVIDER_XXBLACK
                }}
        />
        <img style={{position: "absolute", left: -400, top: -430, width: 1180, height: 1180}}
             src={`/assets/preview/hair/${hairId}.svg`}/>
      </div>
    </StyledBoxColumn>;
  }

  private play(): void {
    if (!this.props.src) {
      return;
    }
    const player = this.props.audio;
    const lipsyncManager = this.lipsyncManager;
    lipsyncManager.connectAudio(player);
    const animate = () => {
      if (!player.paused) {
        requestAnimationFrame(animate);
      } else {
        this.setState({viseme: VISEMES.sil});
        return;
      }
      const now = Date.now();
      let blink = this.state.blink;
      let blinkChangeAt = this.state.blinkChangeAt || now;
      let blinkOnAt = this.state.blinkOnAt || now;
      const dt = now - blinkChangeAt;
      if (dt >= 200) {
        if (blink) {
          blink = false;
          blinkChangeAt = now;
        } else {
          const val = Math.random();
          if (val < (now - blinkOnAt) / 6000) {
            blink = true;
            blinkOnAt = now;
          }
          blinkChangeAt = now;
        }
      }
      lipsyncManager.processAudio();
      const viseme = lipsyncManager.viseme;
      this.setState({blink, blinkChangeAt, blinkOnAt, viseme});
    };
    const startAnimate = () => {
      player.removeEventListener("play", startAnimate);
      animate();
    };
    player.addEventListener("play", startAnimate);
  }

  private drawCanvas(): void {
    const canvas = this.canvasRef.current;
    if (!canvas) {
      return;
    }
    const width = canvas.width;
    const height = canvas.height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, width, height);

    const faceModel = this.state.faceModel;

    // Eyes
    const eyes = faceModel.eyes;
    ctx.fillStyle = "white";
    ctx.strokeStyle = "black";
    ctx.lineWidth = 4;
    const eyeCenter: Point = {x: width / 2, y: height * .45};
    const eyeMaxDist = width / 4;
    const eyeMinDist = width / 4;
    const eyeSize = eyeMinDist;

    const eyeCenterLeft: Point = {x: eyeCenter.x - eyeMinDist / 2 - eyeMaxDist / 4, y: eyeCenter.y};
    const eyeCenterRight: Point = {x: eyeCenter.x + eyeMinDist / 2 + eyeMaxDist / 4, y: eyeCenter.y};

    const leftEye: Point = {x: eyeCenterLeft.x - eyeMaxDist * eyes.separation / 4, y: eyeCenterLeft.y};
    const rightEye: Point = {x: eyeCenterRight.x + eyeMaxDist * eyes.separation / 4, y: eyeCenterRight.y};

    ctx.save();
    Avatar.drawEyePath(ctx, faceModel, leftEye, rightEye, eyeSize);
    ctx.fill();
    ctx.clip();

    ctx.beginPath();
    ctx.fillStyle = "black";
    const lookOffset = eyes.lookOffset * (eyeSize / 2 - eyeSize / 4);
    ctx.roundRect(leftEye.x + lookOffset * Math.cos(eyes.lookAngle) - eyeSize / 4, leftEye.y - lookOffset * Math.sin(eyes.lookAngle) - eyeSize / 4, eyeSize / 2, eyeSize / 2, eyeSize / 4);
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(rightEye.x + lookOffset * Math.cos(eyes.lookAngle) - eyeSize / 4, rightEye.y - lookOffset * Math.sin(eyes.lookAngle) - eyeSize / 4, eyeSize / 2, eyeSize / 2, eyeSize / 4);
    ctx.fill();

    ctx.restore();
    Avatar.drawEyePath(ctx, faceModel, leftEye, rightEye, eyeSize);
    ctx.stroke();

    // Brows
    const brows = faceModel.brows;
    const browSize = eyeSize * 1.1;
    ctx.beginPath();
    ctx.strokeStyle = "black";
    ctx.lineWidth = 16;
    const leftBrow: Point = {x: eyeCenterLeft.x, y: eyeCenterLeft.y - eyeSize / 2 * 1.25 - 80 * brows.offset}
    ctx.moveTo(leftBrow.x - browSize * Math.cos(brows.angle) / 2, leftBrow.y + browSize * Math.sin(brows.angle) / 2);
    ctx.lineTo(leftBrow.x + browSize * Math.cos(brows.angle) / 2, leftBrow.y - browSize * Math.sin(brows.angle) / 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.strokeStyle = "black";
    ctx.lineWidth = 16;
    const rightBrow: Point = {x: eyeCenterRight.x, y: eyeCenterRight.y - eyeSize / 2 * 1.25 - 80 * brows.offset}
    ctx.moveTo(rightBrow.x + browSize * Math.cos(brows.angle) / 2, rightBrow.y + browSize * Math.sin(brows.angle) / 2);
    ctx.lineTo(rightBrow.x - browSize * Math.cos(brows.angle) / 2, rightBrow.y - browSize * Math.sin(brows.angle) / 2);
    ctx.stroke();

    // Mouth
    ctx.save();
    const mouth = faceModel.mouth;
    const lipSize = height / 2;
    const mouthOpenSize = height / 4;
    const leftMouth: Point = {x: eyeCenterLeft.x - eyeSize / 4, y: eyeCenterLeft.y + eyeSize / 2 + 50};
    const rightMouth: Point = {x: eyeCenterRight.x + eyeSize / 4, y: eyeCenterRight.y + eyeSize / 2 + 50};
    Avatar.drawMouthPath(ctx, faceModel, leftMouth, rightMouth, lipSize, mouthOpenSize);
    ctx.clip();


    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, width, height);

    // Teeth
    ctx.fillStyle = "white";
    const teethMiddleY = (lipSize * mouth.upperLip * 2 / 3 + lipSize * mouth.lowerLip * 2 / 3 + mouthOpenSize * mouth.open) / 2;
    ctx.fillRect(0, 0, width, (leftMouth.y + rightMouth.y) / 2 + teethMiddleY - mouthOpenSize * mouth.open);
    ctx.fillRect(0, (leftMouth.y + rightMouth.y) / 2 + teethMiddleY + mouthOpenSize * mouth.open, width, height);

    ctx.strokeStyle = "black";
    ctx.lineWidth = 2;
    for (let i = 1; i < 8; i++) {
      const tbx = leftMouth.x + i * (rightMouth.x - leftMouth.x) / 8;
      ctx.moveTo(tbx, 0);
      ctx.lineTo(tbx, height);
      ctx.stroke();
    }
    ctx.restore();

    // Lip line
    ctx.strokeStyle = "black";
    ctx.lineWidth = 4;
    Avatar.drawMouthPath(ctx, faceModel, leftMouth, rightMouth, lipSize, mouthOpenSize);
    ctx.stroke();
  }

  private static drawEyePath(ctx: CanvasRenderingContext2D, faceModel: FaceModel, leftEye: Point, rightEye: Point, eyeSize: number): void {
    const eyes = faceModel.eyes;
    ctx.beginPath();
    ctx.roundRect(leftEye.x - eyeSize / 2, leftEye.y - eyeSize * eyes.open / 2, eyeSize, eyeSize * eyes.open, eyeSize / 2);
    ctx.roundRect(rightEye.x - eyeSize / 2, rightEye.y - eyeSize * eyes.open / 2, eyeSize, eyeSize * eyes.open, eyeSize / 2);
  }

  private static drawMouthPath(ctx: CanvasRenderingContext2D, faceModel: FaceModel, leftMouth: Point, rightMouth: Point, lipSize: number, mouthOpenSize: number): void {
    const mouth = faceModel.mouth;
    const curveOffsetUpperY = lipSize * mouth.upperLip + mouthOpenSize * mouth.open;
    const curveOffsetLowerY = lipSize * mouth.lowerLip + mouthOpenSize * mouth.open;
    ctx.beginPath();
    ctx.moveTo(leftMouth.x, leftMouth.y + curveOffsetLowerY * mouth.smiling);
    ctx.bezierCurveTo((leftMouth.x + rightMouth.x) * 1 / 4, (leftMouth.y + rightMouth.y) / 2 + curveOffsetUpperY, (leftMouth.x + rightMouth.x) * 3 / 4, (leftMouth.y + rightMouth.y) / 2 + curveOffsetUpperY, rightMouth.x, rightMouth.y + curveOffsetLowerY * mouth.smiling);
    ctx.bezierCurveTo((leftMouth.x + rightMouth.x) * 3 / 4, (leftMouth.y + rightMouth.y) / 2 + curveOffsetLowerY, (leftMouth.x + rightMouth.x) * 1 / 4, (leftMouth.y + rightMouth.y) / 2 + curveOffsetLowerY, leftMouth.x, leftMouth.y + curveOffsetLowerY * mouth.smiling);
  }
}
